import React from 'react';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableChartIcon from '@mui/icons-material/TableChart';
import CircularProgress from '@mui/material/CircularProgress';

// ── Design tokens ─────────────────────────────────────────────────────────────
export const RC = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', green: '#00695c',
};

// ── Shared input style for date fields ────────────────────────────────────────
export const dateInp = {
  border: `1px solid ${RC.cardBorder}`,
  borderRadius: '7px',
  fontSize: '11px',
  height: '28px',
  padding: '0 6px',
  outline: 'none',
  backgroundColor: RC.card,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

// ── Shared button styles ──────────────────────────────────────────────────────
export const refreshBtn = {
  height: '28px',
  padding: '0 12px',
  borderRadius: '7px',
  border: `1px solid ${RC.cardBorder}`,
  backgroundColor: RC.card,
  color: RC.muted,
  fontWeight: 600,
  fontSize: '11px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontFamily: 'inherit',
  boxShadow: RC.shadow,
};

export const excelBtn = {
  height: '28px',
  padding: '0 12px',
  borderRadius: '7px',
  border: 'none',
  backgroundColor: RC.green,
  color: '#fff',
  fontWeight: 700,
  fontSize: '11px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontFamily: 'inherit',
};

// ── Table header cell style ───────────────────────────────────────────────────
export const thStyle = {
  background: '#f0f4f4',
  fontWeight: 700,
  fontSize: '10px',
  padding: '6px 8px',
  borderBottom: `2px solid ${RC.cardBorder}`,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: RC.muted,
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

// ── ReportShell: wraps every report with consistent header + filter bar ────────
/**
 * Props:
 *   title        – report title string
 *   recordCount  – optional number shown in the badge
 *   filters      – JSX for the filter controls (dates, dropdowns, radio buttons)
 *   actions      – JSX for action buttons (Refresh, Excel, etc.)
 *   loading      – boolean, shows spinner in table area when true
 *   children     – the table / content area
 */
function ReportShell({ title, recordCount, filters, actions, loading, children }) {
  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', backgroundColor: RC.bg, padding: '6px', boxSizing: 'border-box' }}>

      {/* ── Page header ── */}
      <div style={{ backgroundColor: RC.card, borderRadius: '10px', boxShadow: RC.shadow, border: `1px solid ${RC.cardBorder}`, marginBottom: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', flexWrap: 'wrap', gap: '8px' }}>
          {/* Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AssessmentIcon style={{ fontSize: 15, color: RC.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: RC.text, fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              {title}
            </span>
            {recordCount !== undefined && (
              <span style={{ fontSize: '10px', color: RC.muted, backgroundColor: RC.accentDim, border: `1px solid ${RC.accentBorder}`, borderRadius: '10px', padding: '1px 7px', marginLeft: '4px' }}>
                {recordCount} records
              </span>
            )}
          </div>

          {/* Filters + Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {filters}
            {actions}
          </div>
        </div>
      </div>

      {/* ── Table card ── */}
      <div style={{ backgroundColor: RC.card, borderRadius: '10px', boxShadow: RC.shadow, border: `1px solid ${RC.cardBorder}`, overflow: 'hidden' }}>
        {/* Table sub-header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${RC.cardBorder}`, backgroundColor: `${RC.accent}0d` }}>
          <TableChartIcon style={{ fontSize: 13, color: RC.accent }} />
          <span style={{ fontSize: '10px', fontWeight: 700, color: RC.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {title}
          </span>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <CircularProgress size={24} style={{ color: RC.accent }} />
          </div>
        ) : (
          <div style={{ maxHeight: '540px', overflowY: 'auto', overflowX: 'auto' }}>
            {children}
          </div>
        )}
      </div>

    </div>
  );
}

export default ReportShell;
